import { useUserAccount } from '@/api/user/queries';
import { ModalsKey } from '@/enums/modalsKey';
import { useModalNavigation } from '@/hooks/modals/useModalNavigation';
import { usePathname } from '@/hooks/usePathname';
import { useSearchParams } from '@starsoft/common/hooks';
import useTranslation from 'next-translate/useTranslation';
import NextLink from 'next/link';
import { useCallback, useRef } from 'react';
import { CoreLinkProps } from './props';

export default function Link({
  as,
  children,
  href,
  replace,
  scroll,
  shallow,
  modal,
  privateRoute = false,
  ...rest
}: CoreLinkProps) {
  const { lang } = useTranslation('common');
  const { getModalLink } = useModalNavigation();
  const { isAuthorized } = useUserAccount();
  const searchParams = useSearchParams();
  const pathname: string = usePathname();
  const prefetchedUrls = useRef<Set<string>>(new Set());

  function getHref() {
    if (privateRoute && !isAuthorized) {
      return `${pathname}#${ModalsKey.Login}`;
    }

    if (modal) {
      return getModalLink(modal, href);
    }

    return `${String(href).startsWith('/') ? href : `/${href}`}`;
  }

  const shouldPrefetch = !modal && !privateRoute;
  const finalHref = getHref();

  const preloadData = useCallback(async () => {
    if (!shouldPrefetch || typeof finalHref !== 'string') return;

    try {
      // Convert the href to the _next/data URL format
      const buildId = window?.__NEXT_DATA__?.buildId;
      const locale = window?.__NEXT_DATA__?.locale || lang;
      const cleanPath = finalHref?.replace?.(/^\//, '')?.replace?.(/\/$/, '');
      const dataUrl = `/_next/data/${buildId}/${locale}${cleanPath ? `/${cleanPath}` : ''}.json${searchParams?.size > 0 ? `?${searchParams?.toString()}` : ''}`;

      // Don't prefetch the same URL twice
      if (prefetchedUrls.current.has(dataUrl)) {
        return;
      }

      console.debug('Preloading:', dataUrl);
      prefetchedUrls.current.add(dataUrl);

      // Use proper caching headers
      const response = await fetch(dataUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'x-nextjs-prefetch': '1',
        },
        cache: 'default',
      });

      if (!response.ok) {
        console.debug('Preload failed with status:', response.status);
        return;
      }

      // Read the response to ensure it's cached by the browser
      await response.json();
      console.debug('Preload successful:', dataUrl);
    } catch (error) {
      console.debug('Preload failed:', error);
    }
  }, [finalHref, shouldPrefetch, lang, searchParams]);

  return (
    //@ts-ignore
    <NextLink
      {...rest}
      as={as}
      href={finalHref}
      lang={lang}
      passHref
      replace={replace}
      scroll={!!modal ? false : scroll}
      aria-disabled={rest?.disabled}
      shallow={!!modal ? true : shallow}
      aria-label={`Link to ${typeof href == 'object' ? JSON.stringify(href) : href}`}
      prefetch={shouldPrefetch}
      // onMouseEnter={preloadData}
      // onTouchStart={preloadData}
    >
      {children}
    </NextLink>
  );
}
